import { AnchorButton } from "@blueprintjs/core";
import React from "react";
import styled from "styled-components";
import {
  GreenButton,
  GreenOutlineButton,
} from "../../r2components/ThemeButton";
import { ReactComponent as SemgrepText } from "../../assets/SemgrepTextDarkTheme.svg";
import Step2 from "./assets/Step2.png";
import Footer from "./components/Footer";
import { Helmet } from "react-helmet";
import IntegrationTags from "./assets/IntegrationTags";
import { ReactComponent as SemgrepLogo } from "../../assets/BrandIcon.svg";
import { SemgrepExamplesPanel } from "./components/SemgrepExamplesPanel";
import EditorWidget from "../editorWidget/EditorWidget";
import { InstallInstructions } from "./components/InstallInstructions";
import Stars from "./components/Stars";
import FindMoreBugs from "./components/FindMoreBugs";
import InfoList from "./components/InfoList";
import SemgrepRepoInfo from "./components/SemgrepRepoInfo";
import LanguageSupport from "./components/LanguageSupport";
import Contributors from "./components/Contributors";
import {
  AnnouncementBanner,
  BannerLink,
} from "./components/AnnouncementBanner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMegaphone } from "@fortawesome/pro-solid-svg-icons";

const HeroGrid = styled.div`
  max-width: calc(var(--global-content-width) + 120px);
  margin: auto;
  padding: var(--global-spacing-xl) 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 40px;
  justify-content: center;
`;

const HalfWidthColumn = styled.div`
  // minus half of gap and padding
  width: calc(50% - 30px);
  min-width: 400px;

  // calculation for when the columns wrap
  // min-width * 2 + padding + gap
  @media screen and (max-width: calc((400px * 2) + 40px + 40px)) {
    min-width: 0;
    width: 100%;
  }
`;

const ContentContainer = styled.div`
  max-width: calc(var(--global-content-width) + 40px);
  padding: 100px 20px;
  margin: auto;
`;

const Subheader = styled.div`
  font-size: var(--global-font-size-xl);
  line-height: 40px;
  margin-bottom: 40px;
`;

const RightFlex = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  row-gap: 40px;
  &:not(:last-child) {
    padding-bottom: 40px;
  }
`;

const LeftFlex = styled(RightFlex)`
  flex-wrap: wrap-reverse;
`;

const SmallerStepsDiv = styled.div`
  min-width: 300px;
  width: calc(100% - 700px - 28px);
  padding-left: 28px; // conteract subhearder text indent
`;

const LargerStepsDiv = styled.div`
  flex-basis: 700px;
`;

const IndentedSubheader = styled.div`
  text-indent: -28px;
  font-size: var(--global-font-size-l);
  line-height: 1.5;
  margin-bottom: 12px;
`;

const Description = styled.div`
  font-size: var(--global-font-size-s);
  line-height: 28px;
  margin-bottom: 20px;
`;

const Container = styled.main`
  background: #ffffff;
  font-size: var(--global-font-size-s);
  line-height: 24px;
  min-height: 80vh;
`;

const PurpleContainer = styled.div`
  background: var(--indigo-gradient-245);
  color: var(--global-light-gray);
  padding-top: var(--global-spacing-navbar-height);
`;

const GrayContainer = styled.div`
  background: var(--global-light-gray);
  font-size: var(--global-font-size-s);
  padding: var(--global-spacing-l);
`;

const ContributorSection = styled.div`
  max-width: calc(var(--global-content-width) + 120px);
  margin: auto;
`;

const LightContainer = styled.div`
  text-align: left;
  background: radial-gradient(
      107.42% 415.71% at 0% 0%,
      rgba(255, 255, 255, 1) 21.93%,
      rgba(155, 175, 198, 0) 79.73%
    ),
    var(--global-light-gray);
  color: var(--global-dark-purple);
`;

const ClipContainer = styled(LightContainer)`
  margin-top: -10%;
  padding-top: 10%;
  clip-path: polygon(0 10vw, 100% 1vw, 100% 100%, 0% 100%);
`;

const InvertedPurpleContainer = styled.div`
  background: var(--indigo-gradient-115);
  color: var(--global-light-gray);
`;

const GetStarted = styled.div`
  text-align: right;
  margin-bottom: 40px;
  // same query as HalfWidthColumn
  @media screen and (max-width: calc((400px * 2) + 40px + 40px)) {
    text-align: left;
  }
`;

const Header = styled.header`
  position: relative;
  max-width: 394px;
  padding-bottom: var(--global-spacing-s);
`;

const Tagline = styled.p`
  font-size: var(--global-font-size-xl);
  line-height: 40px;
  padding-bottom: var(--global-spacing-xl);
`;

const HomeScreen: React.FC<{}> = () => {
  return (
    <Container>
      <Helmet>
        <title>Semgrep</title>
      </Helmet>
      <Stars />
      <PurpleContainer>
        <BannerLink
          href="https://r2c.dev/blog/2022/semgrep-spring-2022-meetup-recap/"
          target="_blank"
          rel="noopener noreferrer"
        >
          <AnnouncementBanner>
            <FontAwesomeIcon
              icon={faMegaphone}
              style={{ transform: "scaleX(-1)" }}
            />{" "}
            ICYMI Recap of Semgrep Spring Meetup — Btw, Save the date! Semgrep
            Summer Meetup June 1!
          </AnnouncementBanner>
        </BannerLink>
        <HeroGrid>
          <HalfWidthColumn>
            <Header>
              <SemgrepText style={{ maxWidth: "100%" }} />
            </Header>
            <Tagline>
              Static analysis at ludicrous speed
              <br />
              Find bugs and enforce code standards
            </Tagline>
            <InfoList />
          </HalfWidthColumn>
          <HalfWidthColumn>
            <GetStarted>
              <GreenButton large href="/getting-started">
                Get Started
              </GreenButton>
              <SemgrepRepoInfo />
            </GetStarted>
            <div
              style={{
                borderRadius: "5px",
                height: "432px",
                width: "100%",
                overflow: "hidden",
              }}
            >
              <EditorWidget snippetId={"ievans:print-to-logger2"} />
            </div>
            <LanguageSupport />
          </HalfWidthColumn>
        </HeroGrid>
      </PurpleContainer>
      <GrayContainer>
        <ContributorSection>
          <Contributors />
        </ContributorSection>
      </GrayContainer>
      <LightContainer>
        <ContentContainer
          style={{ maxWidth: "calc(var(--global-content-width) + 120px)" }}
        >
          <FindMoreBugs />
        </ContentContainer>
      </LightContainer>
      <InvertedPurpleContainer>
        <ContentContainer>
          <Subheader>
            Trying Semgrep &nbsp;
            <SemgrepLogo />
          </Subheader>
          <RightFlex>
            <SmallerStepsDiv>
              <IndentedSubheader>
                1. Try the open source command line tool
              </IndentedSubheader>
              <Description>
                Run with registry rules or your own. Code is analyzed locally
                (not uploaded).
              </Description>
              <GreenOutlineButton
                large
                href="/docs/getting-started/#run-semgrep-locally"
              >
                Install locally
              </GreenOutlineButton>
            </SmallerStepsDiv>
            <LargerStepsDiv>
              <InstallInstructions />
            </LargerStepsDiv>
          </RightFlex>
          <LeftFlex>
            <LargerStepsDiv>
              <img src={Step2} alt="" style={{ width: "100%" }} />
              <IntegrationTags
                style={{ maxWidth: "100%", paddingLeft: "16px" }}
              />
            </LargerStepsDiv>
            <SmallerStepsDiv>
              <IndentedSubheader>2. Scan code in CI/CD</IndentedSubheader>
              <Description>
                Get results at ludicrous speed with diff-aware scans, review
                findings in MR and PR comments, and deploy Semgrep across your
                organization’s projects.
              </Description>
              <GreenOutlineButton large href="/docs/semgrep-ci">
                Add to CI
              </GreenOutlineButton>
            </SmallerStepsDiv>
          </LeftFlex>
        </ContentContainer>
      </InvertedPurpleContainer>
      <ClipContainer>
        <ContentContainer>
          <RightFlex style={{ marginTop: "-60px" }}>
            <SmallerStepsDiv>
              <IndentedSubheader>
                3. Write rules to enforce your own code guardrails
              </IndentedSubheader>
              <Description>
                Go beyond the registry with rules specific to your organization.
              </Description>
              <AnchorButton
                intent="primary"
                large
                style={{ height: "3em" }}
                href="/playground"
              >
                {/* TODO save a page load and use a NavButton from react-router */}
                Try in your browser
              </AnchorButton>
            </SmallerStepsDiv>
            <LargerStepsDiv>
              <SemgrepExamplesPanel />
            </LargerStepsDiv>
          </RightFlex>
        </ContentContainer>
      </ClipContainer>
      <Footer />
    </Container>
  );
};
export default HomeScreen;
